import React from "react";
import { FormControl, Select, MenuItem, SxProps, Theme } from "@mui/material";
import { MuiSelector } from "@ace/ace-ui-web";

export interface FormFieldClassNames {
    formGroup: string;
    labelWrapper: string;
    label: string;
    errorMessage: string;
}

interface BaseFormFieldProps {
    label: string;
    icon?: React.ReactNode;
    error?: string;
    children: React.ReactNode;
    controlId?: string;
    labelStyle?: React.CSSProperties;
}

/**
 * Creates a FormField component bound to a given set of CSS module class names.
 * If `useConditionalLabel` is true, an <label>/<p> element is chosen based on
 * whether a `controlId` was supplied (matching the previous post-shift behaviour).
 */
export const createFormField = (
    classNames: FormFieldClassNames,
    useConditionalLabel = false,
): React.FC<BaseFormFieldProps> => {
    const FormField: React.FC<BaseFormFieldProps> = ({ label, icon, error, children, controlId, labelStyle }) => (
        <div className={classNames.formGroup}>
            <div className={classNames.labelWrapper}>
                {icon}
                {useConditionalLabel && !controlId ? (
                    <p className={classNames.label} style={labelStyle}>{label}</p>
                ) : (
                    <label className={classNames.label} htmlFor={controlId} style={labelStyle}>
                        {label}
                    </label>
                )}
            </div>
            {children}
            {error && <p className={classNames.errorMessage}>{error}</p>}
        </div>
    );
    return FormField;
};

export interface SelectorFieldProps {
    label: string;
    icon?: React.ReactNode;
    placeholder: string;
    value: string | string[];
    onChange: (value: string | string[]) => void;
    options: Array<{ label: string; value: string }>;
    disabled?: boolean;
    error?: string;
    onMouseDown?: () => void;
    multipleSelect?: boolean;
}

export interface SelectorFieldSx {
    selectSx: SxProps<Theme>;
    menuPropsSx: SxProps<Theme>;
    muiSelectorSx: SxProps<Theme>;
    chipStyle?: React.CSSProperties;
    placeholderStyle?: React.CSSProperties;
}

const defaultChipStyle: React.CSSProperties = {
    backgroundColor: '#ffebee',
    color: '#000',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    display: 'inline-block',
};

const renderChip = (
    val: string,
    options: Array<{ label: string; value: string }>,
    chipStyle?: React.CSSProperties,
) => (
    <span key={val} style={{ ...defaultChipStyle, ...chipStyle }}>
        {options.find(opt => opt.value === val)?.label}
    </span>
);

const renderSelectedChips = (
    selected: string[],
    options: Array<{ label: string; value: string }>,
    chipStyle?: React.CSSProperties,
) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {selected.map((val) => renderChip(val, options, chipStyle))}
    </div>
);

const renderMultiSelectValue = (
    selected: string[],
    placeholder: string,
    options: Array<{ label: string; value: string }>,
    sx: SelectorFieldSx,
) => {
    if (selected.length === 0) {
        return <span style={sx.placeholderStyle}>{placeholder}</span>;
    }
    return renderSelectedChips(selected, options, sx.chipStyle);
};

/**
 * Creates a SelectorField component bound to a given FormField wrapper and
 * MUI `sx` styling, so consumers only need to supply their own visuals.
 */
export const createSelectorField = (
    FormField: React.FC<BaseFormFieldProps>,
    sx: SelectorFieldSx,
    fullWidth = false,
): React.FC<SelectorFieldProps> => {
    const SelectorField: React.FC<SelectorFieldProps> = ({
        label,
        icon,
        placeholder,
        value,
        onChange,
        options,
        disabled,
        error,
        onMouseDown,
        multipleSelect,
    }) => (
        <FormField label={label} icon={icon} error={error}>
            <FormControl fullWidth={fullWidth} onMouseDown={onMouseDown}>
                {multipleSelect ? (
                    <Select
                        multiple
                        value={value as string[]}
                        onChange={(e) => onChange(e.target.value as string[])}
                        disabled={disabled}
                        displayEmpty
                        renderValue={(selected) =>
                            renderMultiSelectValue(selected as string[], placeholder, options, sx)
                        }
                        sx={sx.selectSx}
                        MenuProps={{
                            PaperProps: { sx: sx.menuPropsSx },
                            autoFocus: false,
                        }}
                    >
                        {options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                ) : (
                    <MuiSelector
                        placeholder={placeholder}
                        sx={sx.muiSelectorSx}
                        onChange={onChange as (value: string) => void}
                        value={value as string}
                        disabled={disabled}
                        options={options}
                    />
                )}
            </FormControl>
        </FormField>
    );
    return SelectorField;
};

export interface DateTimeFieldClassNames {
    formGroup: string;
    timeHeaderWrapper: string;
    labelWrapper: string;
    label: string;
    errorMessage: string;
}

interface DateTimeFieldProps {
    label: string;
    icon?: React.ReactNode;
    type: "date" | "time";
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    min?: string;
    error?: string;
    editButton?: React.ReactNode;
    disabled?: boolean;
}

const getFieldId = (type: string, label: string) => `${type}-${label.replace(/\W+/g, '-').toLowerCase()}`;

/**
 * Creates a DateTimeField component bound to a given set of CSS module class
 * names. `getLabelStyle` allows consumers to apply per-usage inline styling
 * (e.g. shifting the label when an icon is present).
 */
export const createDateTimeField = (
    classNames: DateTimeFieldClassNames,
    getLabelStyle?: (icon?: React.ReactNode) => React.CSSProperties | undefined,
): React.FC<DateTimeFieldProps> => {
    const DateTimeField: React.FC<DateTimeFieldProps> = ({
        label,
        icon,
        type,
        value,
        onChange,
        min,
        error,
        editButton,
        disabled,
    }) => {
        const fieldId = getFieldId(type, label);
        return (
            <div className={`${classNames.formGroup} form-group`}>
                <div className={editButton ? classNames.timeHeaderWrapper : classNames.labelWrapper}>
                    <div className={classNames.labelWrapper}>
                        {icon}
                        <label className={classNames.label} htmlFor={fieldId} style={getLabelStyle?.(icon)}>
                            {label}
                        </label>
                    </div>
                    {editButton}
                </div>
                <input
                    id={fieldId}
                    type={type}
                    value={value}
                    onChange={onChange}
                    min={min}
                    disabled={disabled}
                    required
                />
                {error && <p className={classNames.errorMessage}>{error}</p>}
            </div>
        );
    };
    return DateTimeField;
};
